import React, { useEffect, useState } from 'react';
import { Download, ShieldCheck, X } from 'lucide-react';
import { useCanPerform } from '../../hooks/useCanPerform';
import AuditLogService from '../../services/admin/auditLog.service';
import { fetchUsers } from '../../services/admin/user.service';
import { fetchPatients } from '../../services/admin/patient.service';

// AUD-03 action filter (audit rows store the action in lower case)
const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All actions' },
  { value: 'view', label: 'View' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'export', label: 'Export' },
  { value: 'download', label: 'Download' },
  { value: 'override', label: 'Override' },
  { value: 'login_failed', label: 'Failed sign-in' },
  { value: 'login_success', label: 'Sign-in' },
];

interface FilterOption { value: string; label: string }

/**
 * HIPAA audit log CSV export (Phase 3, 3.6; owner and admin). Shared by Organization Account and
 * the export-only /account/suspended page (Phase 13).
 *
 * Free on every plan and in every account state (Phase 17 D5, supersedes Phase 3 D9): one request,
 * then the download.
 */
const AuditLogExportSection: React.FC = () => {
  const canDownloadAuditLog = useCanPerform('download:audit:log');
  const today = new Date().toISOString().slice(0, 10);
  const [auditStartDate, setAuditStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  });
  const [auditEndDate, setAuditEndDate] = useState(today);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditDownloadConfirmation, setAuditDownloadConfirmation] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterPatientId, setFilterPatientId] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [userOptions, setUserOptions] = useState<FilterOption[]>([]);
  const [patientOptions, setPatientOptions] = useState<FilterOption[]>([]);

  // AUD-03: user and patient pick-lists (best effort; the report works without them)
  useEffect(() => {
    if (!canDownloadAuditLog) return undefined;
    let cancelled = false;
    fetchUsers(1, 200)
      .then(({ users }) => {
        if (!cancelled) setUserOptions(users.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}`.trim() || u.email })));
      })
      .catch(() => undefined);
    fetchPatients(1, 200)
      .then(({ patients }) => {
        if (!cancelled) setPatientOptions(patients.filter((p) => !p.isSample).map((p) => ({ value: p.id, label: `${p.lastName ?? ''}, ${p.firstName ?? ''}${p.mrn ? ` (${p.mrn})` : ''}` })));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canDownloadAuditLog]);

  if (!canDownloadAuditLog) return null;

  const updateAuditStartDate = (value: string) => {
    const latestAllowedEnd = new Date(`${value}T00:00:00`);
    latestAllowedEnd.setDate(latestAllowedEnd.getDate() + 366);
    setAuditStartDate(value);
    if (auditEndDate > latestAllowedEnd.toISOString().slice(0, 10)) {
      setAuditEndDate(latestAllowedEnd.toISOString().slice(0, 10));
    }
  };

  const requestAuditDownload = async () => {
    if (auditLoading) return;
    setAuditLoading(true);
    setAuditError('');
    try {
      const result = await AuditLogService.requestDownload({
        startDate: auditStartDate,
        endDate: auditEndDate,
        format: 'CSV',
        ...(filterUserId ? { userId: filterUserId } : {}),
        ...(filterPatientId ? { patientId: filterPatientId } : {}),
        ...(filterAction ? { action: filterAction } : {})
      });
      await AuditLogService.downloadFile(result.downloadUrl, result.fileName);
      setAuditDownloadConfirmation(result.fileName);
    } catch (error) {
      setAuditError(error instanceof Error ? error.message : 'Unable to generate audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <>
      <section id="audit-log-export" aria-labelledby="audit-log-heading" className="bg-white rounded-xl border border-slate-200">
        <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-slate-200">
          <div className="flex items-center gap-2"><ShieldCheck size={17} className="text-slate-600" /><h2 id="audit-log-heading" className="text-base font-semibold text-slate-900">HIPAA Audit Log</h2></div>
          <p className="text-sm text-slate-500 mt-1">Export your organization&apos;s patient-access history for compliance records.</p>
        </div>
        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            <label className="block"><span className="block text-xs font-medium text-slate-700 mb-1">Start date</span><input type="date" value={auditStartDate} max={auditEndDate} onChange={(event) => updateAuditStartDate(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500" /></label>
            <label className="block"><span className="block text-xs font-medium text-slate-700 mb-1">End date</span><input type="date" value={auditEndDate} min={auditStartDate} max={today} onChange={(event) => setAuditEndDate(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500" /></label>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            <label className="block"><span className="block text-xs font-medium text-slate-700 mb-1">User</span><select value={filterUserId} onChange={(event) => setFilterUserId(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"><option value="">All users</option>{userOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
            <label className="block"><span className="block text-xs font-medium text-slate-700 mb-1">Patient</span><select value={filterPatientId} onChange={(event) => setFilterPatientId(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"><option value="">All patients</option>{patientOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
            <label className="block"><span className="block text-xs font-medium text-slate-700 mb-1">Action</span><select value={filterAction} onChange={(event) => setFilterAction(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500">{ACTION_OPTIONS.map((o) => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}</select></label>
          </div>
          <p className="mt-3 text-xs text-slate-500">Audit log exports are included on every plan at no charge.</p>
          <button type="button" disabled={auditLoading || auditStartDate > auditEndDate} onClick={requestAuditDownload} className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"><Download size={16} />{auditLoading ? 'Preparing...' : 'Download Audit Log (CSV)'}</button>
          {auditError && <p className="mt-3 text-sm text-red-700" role="alert">{auditError}</p>}
        </div>
      </section>
      {auditDownloadConfirmation && <AuditDownloadConfirmation fileName={auditDownloadConfirmation} onClose={() => setAuditDownloadConfirmation('')} />}
    </>
  );
};

const AuditDownloadConfirmation: React.FC<{ fileName: string; onClose: () => void }> = ({ fileName, onClose }) => <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-labelledby="audit-confirmation-heading"><div className="w-full max-w-md rounded-xl bg-white shadow-xl"><div className="flex items-start justify-between border-b border-slate-200 px-5 py-4"><h2 id="audit-confirmation-heading" className="text-lg font-semibold text-slate-900">Download ready</h2><button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button></div><div className="p-5"><p className="text-sm text-slate-700">Your HIPAA audit log was downloaded successfully.</p><p className="mt-2 break-all text-xs text-slate-500">{fileName}</p><div className="mt-5 flex justify-end"><button type="button" onClick={onClose} className="rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700">Done</button></div></div></div></div>;

export default AuditLogExportSection;
